/**
 * The admin screen: players, devices, invites and adopting old history.
 *
 * Everything here is a thin view over routes that each carry `requireAdmin` on
 * the server. Hiding this page from a player's device is a courtesy, not a
 * control — a child who types the URL gets a screen full of "Alleen de
 * beheerder kan dit."
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Empty, Panel, Screen } from '@/components/ui';
import Avatar from '@/components/Avatar';
import AvatarPicker from '@/components/AvatarPicker';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/store/auth';
import { formatDateTime } from '@/lib/labels';

interface Profile {
  id: string;
  name: string;
  emoji: string;
  avatar: string | null;
  avatarKind: 'photo' | 'drawing' | null;
}

interface Device {
  id: string;
  role: 'player' | 'admin';
  status: 'active' | 'pending' | 'revoked';
  label: string | null;
  profileId: string | null;
  createdAt: string;
  expiresAt: string;
  lastSeenAt: string | null;
  lastSeenIp: string | null;
  userAgent: string | null;
}

interface Invite {
  id: string;
  label: string | null;
  profileId: string | null;
  createdAt: string;
  expiresAt: string;
  redeemedAt: string | null;
  revokedAt: string | null;
}

interface Unclaimed {
  name: string;
  games: number;
  firstSeen: string;
  lastSeen: string;
}

interface NewInvite {
  url: string;
  qr: string;
}

export default function Beheer() {
  const navigate = useNavigate();
  const role = useAuth((state) => state.role);
  const logout = useAuth((state) => state.logout);

  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [unclaimed, setUnclaimed] = useState<Unclaimed[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, d, i, u] = await Promise.all([
        api<Profile[]>('/api/profiles'),
        api<Device[]>('/api/admin/devices'),
        api<Invite[]>('/api/admin/invites'),
        api<Unclaimed[]>('/api/admin/unclaimed'),
      ]);
      setProfiles(p);
      setDevices(d);
      setInvites(i);
      setUnclaimed(u);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Laden mislukt.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Wrap an action so a failure shows up instead of vanishing. */
  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      setBusy(true);
      try {
        await action();
        await load();
        setError(null);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Actie mislukt.');
      } finally {
        setBusy(false);
      }
    },
    [load]
  );

  if (role !== 'admin') {
    return (
      <Screen title="Beheer" back="/">
        <Panel>
          <p className="text-slate-400">
            Dit toestel is geen beheerder.{' '}
            <a href="/beheer/login" className="text-accent-400 underline underline-offset-4">
              Inloggen met Google
            </a>
          </p>
        </Panel>
      </Screen>
    );
  }

  return (
    <Screen
      title="Beheer"
      back="/"
      subtitle="Spelers, toestellen en uitnodigingen"
      actions={
        <Button
          onClick={() =>
            void logout().then(() => {
              navigate('/', { replace: true });
            })
          }
        >
          Afmelden
        </Button>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </p>
        )}

        <Players profiles={profiles} busy={busy} run={run} reload={load} />
        <Invites invites={invites} profiles={profiles} busy={busy} run={run} />
        <Devices devices={devices} profiles={profiles} busy={busy} run={run} />
        <History unclaimed={unclaimed} profiles={profiles} busy={busy} run={run} />
        <Diagnostics />
      </div>
    </Screen>
  );
}

type Run = (action: () => Promise<unknown>) => Promise<void>;

// --- players ---------------------------------------------------------------

function Players({
  profiles,
  busy,
  run,
  reload,
}: {
  profiles: Profile[];
  busy: boolean;
  run: Run;
  reload: () => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const open = profiles.find((profile) => profile.id === editing) ?? null;

  return (
    <Panel title="Spelers">
      {open && (
        <div className="mb-3">
          <AvatarPicker
            profile={open}
            onChanged={() => void reload()}
            onClose={() => setEditing(null)}
          />
        </div>
      )}

      <ul className="space-y-2">
        {profiles.map((profile) => (
          <li
            key={profile.id}
            className="flex items-center gap-3 rounded-xl bg-base-700/60 px-3 py-2"
          >
            <button
              type="button"
              onClick={() => setEditing(editing === profile.id ? null : profile.id)}
              aria-label={`Foto van ${profile.name} wijzigen`}
              className="rounded-full transition hover:opacity-80"
            >
              <Avatar player={profile} size={36} />
            </button>
            <span className="flex-1 font-medium">{profile.name}</span>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                /*
                 * Deliberately a hard confirm with the consequence spelled out.
                 * Deleting a player removes them from old games too, which is
                 * the intended behaviour for a child's name and photo — but not
                 * something to discover afterwards.
                 */
                if (
                  !window.confirm(
                    `${profile.name} verwijderen? De naam blijft in oude potjes staan, maar de speler en straks ook de foto verdwijnen overal.`
                  )
                )
                  return;
                void run(() => api(`/api/admin/profiles/${profile.id}`, { method: 'DELETE' }));
              }}
              className="rounded-lg px-2 py-1 text-sm text-slate-500 hover:text-red-300"
            >
              Verwijderen
            </button>
          </li>
        ))}
        {profiles.length === 0 && <Empty>Nog geen spelers.</Empty>}
      </ul>

      <form
        className="mt-3 flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = name.trim();
          if (!trimmed) return;
          void run(() =>
            api('/api/profiles', { method: 'POST', body: JSON.stringify({ name: trimmed }) })
          ).then(() => setName(''));
        }}
      >
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Naam"
          maxLength={40}
          className="min-h-12 flex-1 rounded-xl bg-base-700 px-3 text-base outline-none placeholder:text-slate-500 focus:ring-2 focus:ring-accent-500"
        />
        <Button type="submit" variant="primary" disabled={busy || !name.trim()}>
          Toevoegen
        </Button>
      </form>
    </Panel>
  );
}

// --- invites ---------------------------------------------------------------

function Invites({
  invites,
  profiles,
  busy,
  run,
}: {
  invites: Invite[];
  profiles: Profile[];
  busy: boolean;
  run: Run;
}) {
  const [fresh, setFresh] = useState<NewInvite | null>(null);
  const [label, setLabel] = useState('');
  const [profileId, setProfileId] = useState('');

  const open = invites.filter((invite) => !invite.redeemedAt && !invite.revokedAt);

  return (
    <Panel title="Uitnodigingen">
      <p className="mb-3 text-sm text-slate-400">
        Laat het toestel de QR-code scannen. Vijftien minuten geldig, één keer te gebruiken — daarna
        hoeft dat toestel nooit meer in te loggen.
      </p>

      <div className="flex flex-wrap gap-2">
        <input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="Welk toestel? (bijv. iPad van Mila)"
          maxLength={40}
          className="min-h-12 min-w-40 flex-1 rounded-xl bg-base-700 px-3 text-base outline-none placeholder:text-slate-500 focus:ring-2 focus:ring-accent-500"
        />
        <select
          value={profileId}
          onChange={(event) => setProfileId(event.target.value)}
          className="min-h-12 rounded-xl bg-base-700 px-3 text-base outline-none focus:ring-2 focus:ring-accent-500"
        >
          <option value="">Geen vaste speler</option>
          {profiles.map((profile) => (
            <option key={profile.id} value={profile.id}>
              {profile.emoji} {profile.name}
            </option>
          ))}
        </select>
        <Button
          variant="primary"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              const created = await api<NewInvite>('/api/admin/invites', {
                method: 'POST',
                body: JSON.stringify({
                  label: label.trim() || undefined,
                  profileId: profileId || undefined,
                }),
              });
              // Shown exactly once — the server keeps only a hash of the code.
              setFresh(created);
              setLabel('');
            })
          }
        >
          Uitnodiging maken
        </Button>
      </div>

      {fresh && (
        <div className="mt-4 rounded-xl border border-accent-500/30 bg-accent-500/5 p-4 text-center">
          <img
            src={fresh.qr}
            alt="QR-code van de uitnodiging"
            className="mx-auto w-56 max-w-full rounded-lg bg-white p-2"
          />
          <p className="mt-3 break-all text-xs text-slate-500">{fresh.url}</p>
          <p className="mt-2 text-sm text-slate-400">
            Deze code is nu voor het laatst te zien. Scan hem, of maak straks een nieuwe.
          </p>
          <button
            type="button"
            onClick={() => setFresh(null)}
            className="mt-2 text-sm text-slate-500 underline underline-offset-4"
          >
            Verbergen
          </button>
        </div>
      )}

      {open.length > 0 && (
        <ul className="mt-4 space-y-2 text-sm">
          {open.map((invite) => (
            <li
              key={invite.id}
              className="flex items-center gap-3 rounded-xl bg-base-700/60 px-3 py-2"
            >
              <span className="flex-1">
                {invite.label ?? 'Zonder naam'}
                <span className="block text-xs text-slate-500">
                  verloopt {formatDateTime(invite.expiresAt)}
                </span>
              </span>
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void run(() => api(`/api/admin/invites/${invite.id}`, { method: 'DELETE' }))
                }
                className="text-slate-500 hover:text-red-300"
              >
                Intrekken
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// --- devices ---------------------------------------------------------------

function Devices({
  devices,
  profiles,
  busy,
  run,
}: {
  devices: Device[];
  profiles: Profile[];
  busy: boolean;
  run: Run;
}) {
  const nameOf = (id: string | null) => profiles.find((profile) => profile.id === id)?.name;
  const active = devices.filter((device) => device.status !== 'revoked');

  return (
    <Panel
      title="Toestellen"
      action={
        active.length > 1 && (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              if (
                !window.confirm(
                  'Alle andere toestellen uitzetten? Iedereen moet dan opnieuw een QR-code scannen.'
                )
              )
                return;
              void run(() => api('/api/admin/devices/revoke-others', { method: 'POST' }));
            }}
            className="text-sm text-slate-500 hover:text-red-300"
          >
            Alles behalve dit toestel
          </button>
        )
      }
    >
      <ul className="space-y-2 text-sm">
        {active.map((device) => (
          <li
            key={device.id}
            className="flex items-start gap-3 rounded-xl bg-base-700/60 px-3 py-2"
          >
            <span className="flex-1">
              <span className="font-medium">
                {device.label ?? nameOf(device.profileId) ?? 'Toestel'}
              </span>
              {device.role === 'admin' && (
                <span className="ml-2 rounded bg-accent-500/20 px-1.5 py-0.5 text-xs text-accent-300">
                  beheer
                </span>
              )}
              <span className="block text-xs text-slate-500">
                {device.lastSeenAt
                  ? `laatst gezien ${formatDateTime(device.lastSeenAt)}`
                  : 'nog niet gebruikt'}
                {' · '}
                verloopt {formatDateTime(device.expiresAt)}
              </span>
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                if (!window.confirm('Dit toestel uitzetten?')) return;
                void run(() => api(`/api/admin/devices/${device.id}`, { method: 'DELETE' }));
              }}
              className="text-slate-500 hover:text-red-300"
            >
              Uitzetten
            </button>
          </li>
        ))}
        {active.length === 0 && <Empty>Nog geen toestellen toegelaten.</Empty>}
      </ul>
      <p className="mt-3 text-xs text-slate-500">
        Uitzetten werkt meteen, bij het eerstvolgende verzoek van dat toestel. Wat er al op staat
        blijft er wel op — een uitgezet toestel houdt zijn eigen potjes.
      </p>
    </Panel>
  );
}

// --- adopting history ------------------------------------------------------

function History({
  unclaimed,
  profiles,
  busy,
  run,
}: {
  unclaimed: Unclaimed[];
  profiles: Profile[];
  busy: boolean;
  run: Run;
}) {
  if (unclaimed.length === 0) return null;

  return (
    <Panel title="Oude potjes koppelen">
      <p className="mb-3 text-sm text-slate-400">
        Deze namen staan in opgeslagen potjes maar horen nog bij geen speler. Koppelen telt die
        potjes mee in de ranglijst.{' '}
        <span className="text-slate-500">
          Let op: twee verschillende mensen die dezelfde naam gebruikten zijn in de data niet uit
          elkaar te houden — koppelen voegt ze samen.
        </span>
      </p>

      <ul className="space-y-2 text-sm">
        {unclaimed.map((row) => (
          <li
            key={row.name}
            className="flex items-center gap-3 rounded-xl bg-base-700/60 px-3 py-2"
          >
            <span className="flex-1">
              <span className="font-medium">{row.name}</span>
              <span className="block text-xs text-slate-500">
                {row.games} {row.games === 1 ? 'potje' : 'potjes'} · {formatDateTime(row.firstSeen)}{' '}
                tot {formatDateTime(row.lastSeen)}
              </span>
            </span>
            <select
              defaultValue=""
              disabled={busy || profiles.length === 0}
              onChange={(event) => {
                const chosen = event.target.value;
                if (!chosen) return;
                void run(() =>
                  api('/api/admin/unclaimed', {
                    method: 'POST',
                    body: JSON.stringify({ name: row.name, profileId: chosen }),
                  })
                );
              }}
              className="min-h-10 rounded-lg bg-base-600 px-2 text-sm outline-none"
            >
              <option value="">Koppel aan…</option>
              {profiles.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.name}
                </option>
              ))}
            </select>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

// --- diagnostics -----------------------------------------------------------

function Diagnostics() {
  const [health, setHealth] = useState<{
    liveRooms: number;
    uptime: number;
    mode: string;
    googleConfigured: boolean;
  } | null>(null);

  useEffect(() => {
    void api<NonNullable<typeof health>>('/api/admin/health')
      .then(setHealth)
      .catch(() => setHealth(null));
  }, []);

  if (!health) return null;

  const hours = Math.floor(health.uptime / 3600);
  const minutes = Math.floor((health.uptime % 3600) / 60);

  return (
    <Panel title="Server">
      <dl className="grid grid-cols-2 gap-2 text-sm text-slate-400">
        <dt>Lopende potjes</dt>
        <dd className="text-right text-white">{health.liveRooms}</dd>
        <dt>Draait al</dt>
        <dd className="text-right text-white">
          {hours}u {minutes}m
        </dd>
        <dt>Modus</dt>
        <dd className="text-right text-white">{health.mode}</dd>
        <dt>Google ingesteld</dt>
        <dd className="text-right text-white">{health.googleConfigured ? 'ja' : 'nee'}</dd>
      </dl>
    </Panel>
  );
}
